import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import * as yaml from 'js-yaml';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { extractSchemaFields, debounce, createNewParameter, filterParameters, normalizeCodeName, determineParameterScope, isValidCodeName, isPiiField, parseVendorSchemaFromYaml } from '../../utils/schemaUtils';
import { toFriendlyName } from '../../utils/schemaUtils';
import { ActionButton } from '../../components/LabsAppBar';
import EventListPanel from '../../components/schema/EventListPanel';
import SchemaEditorLayout from '../../components/schema/SchemaEditorLayout';
import SchemaEditorDialogs from '../../components/schema/SchemaEditorDialogs';
import SchemaPropertiesPanel from '../../components/schema/SchemaPropertiesPanel';
import { Parameter, EventSchema, UnifiedSchema, FilterOptions, Conflict, NonStandardField, ImportResolution, SchemaType } from '../../types/schema';
import { createUnifiedSchema, updateUnifiedSchema, fetchAllSchemaNames, fetchAllUnifiedSchemas, fetchUnifiedSchema } from '../../services/schemaDefinitionApiService';
import { integrationService } from '../../services/integration.service';
import { Integration } from '../../types/integration';
import { loadStateSchemaEditor, clearStateSchemaEditor } from '../../store/persistence';
import { useNotifications } from '../../contexts/NotificationContext';

const getSchemaTypeIcon = (type: SchemaType | string): string => {
  switch (type) {
    case 'vendor_schema':
      return 'Package';
    case 'custom_schema':
      return 'FileChartColumn';
    case 'integration_schema':
      return 'ShieldCheck';
    default:
      return 'FileChartColumn';
  }
};

const getSchemaTypeDisplayName = (type: SchemaType | string): string => {
  const friendlyName = toFriendlyName(type || '').replace(' Schema', '');
  return `${friendlyName} Schemas`;
};

const SchemaEditPage: React.FC = () => {
  const { showNotification } = useNotifications();
  const [unifiedSchema, setUnifiedSchema] = useState<UnifiedSchema | null>(null);
  const unifiedSchemaRef = useRef(unifiedSchema);
  const [selectedEvent, setSelectedEvent] = useState<EventSchema | null>(null);
  const [globalTreeExpansion, setGlobalTreeExpansion] = useState<Record<string, boolean>>({});
  const [eventSpecificTreeExpansion, setEventSpecificTreeExpansion] = useState<Record<string, boolean>>({});
  const [isDragOver, setIsDragOver] = useState(false);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [allSchemaNames, setAllSchemaNames] = useState<string[]>([]);

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showConfirmCancelDialog, setShowConfirmCancelDialog] = useState(false);
  const [showSaveAsDraftDialog, setShowSaveAsDraftDialog] = useState(false);
  const [showSaveAsPublishedDialog, setShowSaveAsPublishedDialog] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<EventSchema | null>(null);
  const [paramToEditId, setParamToEditId] = useState<string | null>(null);
  const [activeEditingParamId, setActiveEditingParamId] = useState<string | null>(null);
  const [showDeletePropertyConfirmation, setShowDeletePropertyConfirmation] = useState(false);
  const [propertyToDeleteFullPath, setPropertyToDeleteFullPath] = useState<string | null>(null);
  // Track the count of parameters to force re-render when parameters are added
  const [parameterCount, setParameterCount] = useState<number>(0);

  // State for ImportConflictDialog
  const [showImportConflictDialog, setShowImportConflictDialog] = useState(false);
  const [conflictingParameters, setConflictingParameters] = useState<Conflict[]>([]);
  const [nonStandardFields, setNonStandardFields] = useState<NonStandardField[]>([]);
  const [pendingImportFiles, setPendingImportFiles] = useState<File[]>([]); // Store files for re-processing after dialog

  // State for Persistence Conflict Dialog
  const [showPersistenceConflictDialog, setShowPersistenceConflictDialog] = useState(false);
  const [backendSchemaOnLoad, setBackendSchemaOnLoad] = useState<UnifiedSchema | null>(null);

  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    searchTerm: '',
    showGlobal: true,
    showEventSpecific: true,
    dataType: null,
    priority: null,
  });
  const [showFilterPanel] = useState(false); // Keep this state here, pass to PropertiesHeader

  const navigate = useNavigate();
  const { schemaName, schemaType } = useParams<{ schemaName: string, schemaType: string }>();
  const location = useLocation();
    const { sourceName: nameFromState, newSchema: isNewSchemaCreation, forceLoadFromBackend } = (location.state || {}) as { sourceName?: string, newSchema?: boolean, forceLoadFromBackend?: boolean };

  // Event Name, Code Name, Description editing states
  const [isEventNameEditing, setIsEventNameEditing] = useState(false);
  const [isEventCodeNameEditing, setIsEventCodeNameEditing] = useState(false); // Re-added
  const [isEventDescriptionEditing, setIsEventDescriptionEditing] = useState(false);
  const eventNameInputRef = useRef<HTMLInputElement>(null);
  const eventCodeNameInputRef = useRef<HTMLInputElement>(null); // Re-added
  const eventDescriptionInputRef = useRef<HTMLTextAreaElement>(null);
  const [isEventCodeNameManuallyEdited, setIsEventCodeNameManuallyEdited] = useState(false);
  const [eventCodeNameError, setEventCodeNameError] = useState<string | null>(null); // Re-added, but will be used for validation

  useEffect(() => {
    unifiedSchemaRef.current = unifiedSchema;
    // Reset manual edit flag when selected event changes
    setIsEventCodeNameManuallyEdited(false);
    setEventCodeNameError(null); // Clear error on event change
  }, [unifiedSchema, selectedEvent]); // Added selectedEvent to dependencies

  // Ensure selectedEvent is always in sync with the unifiedSchema
  useEffect(() => {
    if (unifiedSchema && selectedEvent) {
      const updatedSelectedEvent = unifiedSchema.events.find(e => e.id === selectedEvent.id);
      if (updatedSelectedEvent && updatedSelectedEvent !== selectedEvent) {
        setSelectedEvent(updatedSelectedEvent);
      }
    }
  }, [unifiedSchema, selectedEvent]);

  const [isLoading, setIsLoading] = useState(true);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
  const isDesktopMedium = useMediaQuery('(max-width: 1600px)'); // For screens up to 1600px wide
  const isTablet = useMediaQuery('(max-width: 1280px)'); // For screens up to 1280px wide
  const isMobile = useMediaQuery('(max-width: 1024px)'); // For screens up to 1024px wide

  // Persistence will be managed explicitly within save/load logic.

  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  const debouncedSave = useMemo(
    () => debounce(async (schemaToSave: UnifiedSchema) => { // Now saves UnifiedSchema
      let resultSchema: UnifiedSchema | null = null;
      try {
        if (schemaToSave.isNew) {
          resultSchema = await createUnifiedSchema(schemaToSave);
        } else {
          resultSchema = await updateUnifiedSchema(schemaToSave);
        }

        if (resultSchema) {
          setUnifiedSchema(resultSchema); // Update the unified schema state
          setHasUnsavedChanges(false); // Mark as saved
          clearStateSchemaEditor(resultSchema.id); // Clear local storage for this schema after successful save

          // If the selected event's ID is still valid in the new schema, re-select it
          if (selectedEvent && resultSchema.events.some(e => e.id === selectedEvent.id)) {
            setSelectedEvent(resultSchema.events.find(e => e.id === selectedEvent.id) || null);
          } else if (resultSchema.events.length > 0) {
            // Otherwise, select the first event if available
            setSelectedEvent(resultSchema.events[0]);
          } else {
            setSelectedEvent(null); // No events left
          }
        } else {
          console.error('Failed to save unified schema definition.');
          showNotification('error', 'Failed to save schema.'); // Add error notification
        }
      } catch (error) {
        console.error('Error saving unified schema definition:', error);
        showNotification('error', 'Error saving schema.'); // Add error notification for exceptions
      } finally {
        setIsSavingDraft(false); // Set draft saving state to false
      }
    }, 1000),
    [selectedEvent, showNotification]
  );

  // Make loadUnifiedSchema a useCallback to stabilize it
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<string | null>(null);

  const loadUnifiedSchema = useCallback(async () => {
    setIsLoading(true);

    if (!schemaType) {
      console.error("Schema type is missing from URL. Cannot load or create schema.");
      showNotification('error', 'Schema type is missing from the URL.');
      navigate('/labs/settings', { replace: true });
      setIsLoading(false);
      return;
    }

    const names = await fetchAllSchemaNames();
    setAllSchemaNames(names);

    // Fetch integrations
    try {
      const { data: fetchedIntegrations } = await integrationService.getIntegrations();
      setIntegrations(fetchedIntegrations);
    } catch (error) {
      console.error('Error fetching integrations:', error);
      showNotification('error', 'Failed to load integrations.');
    }

    let fetchedUnifiedSchema: UnifiedSchema | null = null;
    let foundEventInFetchedSchema: EventSchema | null = null;

    // Case 1: Creating a brand new schema (schemaName is 'new' or newSchema flag from state)
    if (schemaName === 'new' || isNewSchemaCreation) {
      const newUnifiedSchema: UnifiedSchema = {
        id: uuidv4(),
        name: '',
        codeName: '',
        description: '',
        events: [],
        parameters: [],
        isNew: true,
        status: 'draft',
        type: schemaType as SchemaType,
        integrationId: undefined, // Initialize integrationId
      };
      fetchedUnifiedSchema = newUnifiedSchema;
      setHasUnsavedChanges(true);
      clearStateSchemaEditor(); // Clear all schema editor states when creating a new schema
    }
    // Case 2: Loading an existing schema by its UnifiedSchema ID (schemaName is present and not 'new')
    else if (schemaName) {
      try {
        // Always fetch from backend first for existing schemas
        fetchedUnifiedSchema = await fetchUnifiedSchema(schemaName, schemaType as SchemaType);
        setHasUnsavedChanges(false); // Freshly loaded from backend, no unsaved changes initially

        // After fetching, check if there's a persisted state for this specific schema ID
        const persistedState = loadStateSchemaEditor(schemaName);

        // If persisted state exists and we are not explicitly forcing a backend load,
        // show the conflict dialog.
        if (persistedState && persistedState.unifiedSchema && !forceLoadFromBackend) {
          setBackendSchemaOnLoad(fetchedUnifiedSchema); // Store the backend version
          setShowPersistenceConflictDialog(true); // Show dialog to let user choose
          setIsLoading(false); // Stop loading until user makes a choice
          return; // Exit early, state will be set by dialog handlers
        } else if (persistedState && persistedState.unifiedSchema && forceLoadFromBackend) {
          // If persisted state exists but we are forcing backend load, clear it
          clearStateSchemaEditor(schemaName);
        } else if (!persistedState || !persistedState.unifiedSchema) {
          // If no persisted state, clear any old state for this schema ID to ensure clean slate
          clearStateSchemaEditor(schemaName);
        }

        // If we reach here, either there was no persisted state, or user chose to discard it,
        // or forceLoadFromBackend was true. Proceed with fetchedUnifiedSchema.
        // No changes needed to fetchedUnifiedSchema here, it's already the backend version.

        if (fetchedUnifiedSchema) {
          setSelectedIntegrationId(fetchedUnifiedSchema.integrationId || null); // Set selected integration
        }

        if (fetchedUnifiedSchema && fetchedUnifiedSchema.events.length > 0) {
          foundEventInFetchedSchema = fetchedUnifiedSchema.events[0]; // Select the first event by default
        } else if (!fetchedUnifiedSchema) {
          console.warn(`Unified schema with ID: ${schemaName} not found. Redirecting to definitions.`);
          showNotification('error', `Schema with ID ${schemaName} not found.`);
          navigate('/labs/settings/definitions', { replace: true });
          setIsLoading(false);
          return; // Exit early
        }
      } catch (error) {
        console.error(`Error loading unified schema with ID ${schemaName}:`, error);
        showNotification('error', `Error loading schema with ID ${schemaName}.`);
        navigate('/labs/settings/definitions', { replace: true }); // Redirect on error
        setIsLoading(false);
        return; // Exit early
      }
    }
    // Case 3: No schemaName and no new schema creation flag (e.g., direct access to /labs/schema-edit without ID)
    else {
      // This case should ideally not happen if routing is strict, but as a fallback:
      // Try to load from persistence first
      try {
        // Try to load from persistence first, but we need a schema ID
        // Since we don't have a schemaName, we'll try to get all schemas and use the first one
        const allSchemas = await fetchAllUnifiedSchemas(schemaType as SchemaType);
        if (allSchemas.length > 0) {
          const firstSchemaId = allSchemas[0].id;
          const persistedState = loadStateSchemaEditor(firstSchemaId);
          if (persistedState && persistedState.unifiedSchema) {
            fetchedUnifiedSchema = persistedState.unifiedSchema;
            setHasUnsavedChanges(true);
            if (fetchedUnifiedSchema.events.length > 0) {
              foundEventInFetchedSchema = fetchedUnifiedSchema.events[0];
            }
          } else {
            // No persisted state for the first schema, just use it directly
            fetchedUnifiedSchema = allSchemas[0];
            if (fetchedUnifiedSchema.events.length > 0) {
              foundEventInFetchedSchema = allSchemas[0].events[0];
            }
          }
          if (fetchedUnifiedSchema) {
            setSelectedIntegrationId(fetchedUnifiedSchema.integrationId || null); // Set selected integration
          }
        } else {
          // If no schemas were found, create a new empty one.
          console.warn("No schemas found. Creating a new empty schema.");
          const newUnifiedSchema: UnifiedSchema = {
            id: uuidv4(), name: '', codeName: '', description: '', events: [], parameters: [], isNew: true, status: 'draft', type: schemaType as SchemaType, integrationId: undefined,
          };
          fetchedUnifiedSchema = newUnifiedSchema;
          setHasUnsavedChanges(true);
        }
      } catch (error) {
        console.error("Error in fallback loading logic:", error);
        showNotification('error', "Error loading schema. Creating a new one.");
          const newUnifiedSchema: UnifiedSchema = {
            id: uuidv4(), name: '', codeName: '', description: '', events: [], parameters: [], isNew: true, status: 'draft', type: schemaType as SchemaType, integrationId: undefined,
          };
        fetchedUnifiedSchema = newUnifiedSchema;
        setHasUnsavedChanges(true);
      }
    }

    // Final state setting
    if (fetchedUnifiedSchema) {
      setUnifiedSchema(fetchedUnifiedSchema);
      // setHasUnsavedChanges is already handled in the loading branches
      setSelectedEvent(foundEventInFetchedSchema || null);
      // If no event was found/selected, and there are events in the fetched schema, select the first one
      if (!foundEventInFetchedSchema && fetchedUnifiedSchema.events.length > 0) {
        setSelectedEvent(fetchedUnifiedSchema.events[0]);
      }
    }
    setIsLoading(false);
  }, [schemaName, schemaType, isNewSchemaCreation, showNotification, navigate, forceLoadFromBackend]);

  const handleConfirmLoadBackend = useCallback(() => {
    if (backendSchemaOnLoad) {
      clearStateSchemaEditor(backendSchemaOnLoad.id); // Clear persisted state
      setUnifiedSchema(backendSchemaOnLoad); // Load backend version
      setSelectedEvent(backendSchemaOnLoad.events.length > 0 ? backendSchemaOnLoad.events[0] : null);
      setHasUnsavedChanges(false); // No unsaved changes as we loaded backend
      showNotification('info', 'Discarded unsaved changes and loaded last saved version.');
    }
    setShowPersistenceConflictDialog(false);
    setBackendSchemaOnLoad(null);
  }, [backendSchemaOnLoad, showNotification]);

  const handleConfirmLoadPersisted = useCallback(() => {
    if (schemaName) {
      const persistedState = loadStateSchemaEditor(schemaName);
      if (persistedState && persistedState.unifiedSchema) {
        setUnifiedSchema(persistedState.unifiedSchema); // Load persisted version
        setSelectedEvent(persistedState.unifiedSchema.events.length > 0 ? persistedState.unifiedSchema.events[0] : null);
        setHasUnsavedChanges(true); // Mark as unsaved as we loaded persisted changes
        showNotification('info', 'Loaded unsaved changes from your last session.');
      }
    }
    setShowPersistenceConflictDialog(false);
    setBackendSchemaOnLoad(null);
  }, [schemaName, showNotification]);

  // Call loadUnifiedSchema on mount and when schemaName changes
  useEffect(() => {
    loadUnifiedSchema();
  }, [loadUnifiedSchema, schemaName, isNewSchemaCreation]);

  const handleEventChange = useCallback((eventId: string, field: keyof EventSchema, value: string) => {
    setHasUnsavedChanges(true); // Mark as unsaved
    setUnifiedSchema((prevUnifiedSchema: UnifiedSchema | null) => {
      if (!prevUnifiedSchema) return null;
      const updatedEvents = prevUnifiedSchema.events.map((event: EventSchema) => {
        if (event.id === eventId) {
          let updatedEvent = { ...event, [field]: value };

          // Logic for auto-generating codeName
          if (field === 'name') {
            // Only auto-generate codeName if it has not been manually edited
            if (!isEventCodeNameManuallyEdited) {
              updatedEvent.codeName = normalizeCodeName(value);
            }
          } else if (field === 'codeName') {
            // If codeName is explicitly changed, mark as manually edited
            setIsEventCodeNameManuallyEdited(true);
            // Add validation for codeName
            if (value.trim() === '') {
              setEventCodeNameError("Code Name can't be empty");
            } else if (!isValidCodeName(value)) {
              setEventCodeNameError('Must contain only letters, numbers, underscores, hyphens, or spaces');
            } else {
              setEventCodeNameError(null);
            }
          }
          return updatedEvent;
        }
        return event;
      });
      const updatedUnifiedSchema = { ...prevUnifiedSchema, events: updatedEvents };
      return updatedUnifiedSchema;
    });
    if (selectedEvent && selectedEvent.id === eventId) {
      setSelectedEvent((prevSelectedEvent: EventSchema | null) => {
        if (!prevSelectedEvent) return null;
        let updated = { ...prevSelectedEvent, [field]: value };

        // Mirror the logic for selectedEvent state
        if (field === 'name') {
          if (!isEventCodeNameManuallyEdited) {
            updated.codeName = normalizeCodeName(value);
          }
        } else if (field === 'codeName') {
          // No need to set flags here, as they are set in the main setUnifiedSchema block
          // Validation is also handled there.
        }
        return updated;
      });
    }
  }, [selectedEvent, isEventCodeNameManuallyEdited]);


  const buildTreeStructure = useCallback((parameters: Parameter[], expansionState: Record<string, boolean>): [Parameter[], Map<string, Parameter>] => {
    const allNodes: Map<string, Parameter> = new Map();
    const rootNodes: Parameter[] = [];

    // First pass: Populate the map with all parameters, ensuring children array is initialized
    parameters.forEach((param) => {
      const node: Parameter = {
        ...param,
        children: [], // Ensure children array is always initialized
        isExpanded: expansionState[param.fullPath] ?? true, // Use fullPath for expansion state
        level: param.parentPaths?.length || 0, // Calculate level based on parentPaths
      };
      allNodes.set(node.fullPath, node);
    });

    // Second pass: Build the tree by assigning children and identifying root nodes
    allNodes.forEach((node: Parameter) => {
      if (node.parentPaths && node.parentPaths.length > 0) {
        const parentFullPath = node.parentPaths.join('.'); // Corrected: Use join to get full path of parent
        const parentNode = allNodes.get(parentFullPath);
        if (parentNode) {
          parentNode.children = [...(parentNode.children || []), node];
        } else {
          rootNodes.push(node); // Treat as root if parent not found
        }
      } else {
        // This is a root node (no parentPaths or empty parentPaths)
        rootNodes.push(node);
      }
    });

    // Third pass: Sort children and set isLastChild for consistent rendering
    allNodes.forEach((node: Parameter) => {
      if (node.children) {
        node.children.sort((a, b) => a.codeName.localeCompare(b.codeName)); // Sort children by codeName
        node.children.forEach((child: Parameter, index: number) => {
          child.isLastChild = (index === node.children!.length - 1);
        });
      }
    });

    // Sort root nodes for consistent order
    rootNodes.sort((a, b) => a.codeName.localeCompare(b.codeName));

    return [rootNodes, allNodes];
  }, []);

  const toggleTreeNode = useCallback((path: string, type: 'global' | 'eventSpecific') => {
    if (type === 'global') {
      setGlobalTreeExpansion((prev: Record<string, boolean>) => ({
        ...prev,
        [path]: !prev[path]
      }));
    } else {
      setEventSpecificTreeExpansion((prev: Record<string, boolean>) => ({
        ...prev,
        [path]: !prev[path]
      }));
    }
  }, []);

  const handleDuplicateEvent = useCallback((eventToDuplicate: EventSchema) => {
    if (!unifiedSchema) return;

    setHasUnsavedChanges(true);

    // Create a new ID for the duplicated event
    const newEventId = uuidv4();
    const newEventName = `Copy of ${eventToDuplicate.name}`;
    const newEventCodeName = normalizeCodeName(newEventName);

    // Duplicate parameters associated with the event
    const duplicatedParameters: Parameter[] = [];
    const oldToNewParamFullPathMap = new Map<string, string>();

    eventToDuplicate.parameterFullPaths.forEach(oldFullPath => {
      const originalParam = unifiedSchema.parameters.find(p => p.fullPath === oldFullPath);
      if (originalParam) {
        const newParamId = uuidv4();
        const newParamCodeName = originalParam.codeName; // Keep original codeName for duplication
        const newParamFullPath = `${newEventCodeName}.${newParamCodeName}`; // New fullPath based on new event codeName

        const duplicatedParam: Parameter = {
          ...originalParam,
          id: newParamId,
          fullPath: newParamFullPath,
          path: newParamFullPath, // Path is also updated
          parentPaths: [newEventCodeName], // Parent path is the new event's codeName
          scope: 'eventSpecific', // Duplicated parameters are always event-specific
        };
        duplicatedParameters.push(duplicatedParam);
        oldToNewParamFullPathMap.set(oldFullPath, newParamFullPath);
      }
    });

    // Create the new event with updated parameter full paths
    const newEvent: EventSchema = {
      ...eventToDuplicate,
      id: newEventId,
      name: newEventName,
      codeName: newEventCodeName,
      parameterFullPaths: Array.from(oldToNewParamFullPathMap.values()), // Use the new fullPaths
      isNew: true,
    };

    const updatedEvents = [...unifiedSchema.events, newEvent];
    const updatedParameters = [...unifiedSchema.parameters, ...duplicatedParameters];

    const updatedUnifiedSchema = { ...unifiedSchema, events: updatedEvents, parameters: updatedParameters };
    setUnifiedSchema(updatedUnifiedSchema);
    setSelectedEvent(newEvent); // Select the newly duplicated event
    setParameterCount((count: number) => count + 1); // Force re-render of the tree
    showNotification('success', `Event "${eventToDuplicate.name}" duplicated successfully.`);
  }, [unifiedSchema, setUnifiedSchema, setSelectedEvent, setParameterCount, showNotification]);

  const handleDeleteEvent = useCallback((event: EventSchema) => {
    setEventToDelete(event);
    setShowConfirmDialog(true);
  }, []);

  const confirmDeleteEvent = useCallback(async () => {
    if (eventToDelete && unifiedSchema) { // Ensure unifiedSchema exists
      const updatedEvents = unifiedSchema.events.filter(event => event.id !== eventToDelete.id);

      // Determine which parameters are still referenced by the remaining events
      const referencedParams = new Set<string>();
      updatedEvents.forEach((event: EventSchema) => {
        event.parameterFullPaths.forEach((fullPath: string) => {
          referencedParams.add(fullPath);
        });
      });

      // Filter out parameters that are no longer referenced by any event
      const updatedParameters = unifiedSchema.parameters.filter((param: Parameter) =>
        referencedParams.has(param.fullPath)
      );

      const updatedUnifiedSchema = { ...unifiedSchema, events: updatedEvents, parameters: updatedParameters };
      setUnifiedSchema(updatedUnifiedSchema); // Update the unified schema state
      setHasUnsavedChanges(true); // Mark as unsaved
      setParameterCount((count: number) => count + 1); // Force re-render of the tree
      
      // Update selected event if the deleted event was selected
      if (selectedEvent?.id === eventToDelete.id) {
        setSelectedEvent(updatedEvents.length > 0 ? updatedEvents[0] : null);
      }
      
      setEventToDelete(null);
      setShowConfirmDialog(false);
    }
  }, [eventToDelete, unifiedSchema, selectedEvent, setParameterCount]);

  const cancelDeleteEvent = useCallback(() => {
    setShowConfirmDialog(false);
    setEventToDelete(null);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const processFiles = useCallback(async (files: File[], resolution?: ImportResolution) => {
    if (!unifiedSchema) return;

    const newEventsWithParams: { event: EventSchema; params: Parameter[] }[] = [];
    const detectedConflictingParameters: Conflict[] = [];
    const detectedNonStandardFields: NonStandardField[] = [];

    for (const file of files) {
      const isJson = file.type === 'application/json' || file.name.endsWith('.json');
      const isYaml = file.name.endsWith('.yml') || file.name.endsWith('.yaml');

      if (isJson || isYaml) {
        try {
          const content = await file.text();
          const parsedContent = isYaml ? yaml.load(content) : JSON.parse(content);

          if (isYaml && schemaType === 'vendor_schema') {
            const { events: parsedEvents, parameters: parsedParameters } = parseVendorSchemaFromYaml(parsedContent);

            parsedEvents.forEach(event => {
              const eventParams = parsedParameters.filter(p => event.parameterFullPaths.includes(p.fullPath));
              newEventsWithParams.push({ event, params: eventParams });
            });

            parsedParameters.forEach(newParam => {
              const existingParam = unifiedSchema.parameters.find(p => p.fullPath === newParam.fullPath);
              if (existingParam && !detectedConflictingParameters.some(c => c.newParam.fullPath === newParam.fullPath)) {
                detectedConflictingParameters.push({ existingParam, newParam });
              }
            });
          } else {
            const [extractedParamsForFile, currentNonStandardFields] = extractSchemaFields(parsedContent);

            currentNonStandardFields.forEach(field => {
              if (!detectedNonStandardFields.some(f => f.field === field.field && f.path === field.path)) {
                detectedNonStandardFields.push(field);
              }
            });

            const eventName = file.name.replace(/\.(json|yml|yaml)$/, '');
            const newEvent: EventSchema = {
              id: uuidv4(),
              name: toFriendlyName(eventName),
              codeName: normalizeCodeName(eventName),
              description: '',
              parameterFullPaths: extractedParamsForFile.map(p => p.fullPath),
              isNew: true,
            };
            newEventsWithParams.push({ event: newEvent, params: extractedParamsForFile });

            extractedParamsForFile.forEach(newParam => {
              const existingParam = unifiedSchema.parameters.find(p => p.fullPath === newParam.fullPath);
              if (existingParam) {
                detectedConflictingParameters.push({ existingParam, newParam });
              }
            });
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred.';
          showNotification('error', `Invalid file: ${file.name}. ${errorMessage}`);
        }
      }
    }

    // If conflicts or non-standard fields are detected and no resolution is provided yet,
    // show the conflict dialog and defer processing.
    if ((detectedConflictingParameters.length > 0 || detectedNonStandardFields.length > 0) && !resolution) {
      setConflictingParameters(detectedConflictingParameters);
      setNonStandardFields(detectedNonStandardFields);
      setPendingImportFiles(files); // Store files for re-processing
      setShowImportConflictDialog(true);
      return; // Stop further processing until user resolves
    }

    // Proceed with merging based on resolution or if no conflicts/non-standard fields
    const allUniqueParametersMap = new Map<string, Parameter>();

    // Add existing parameters to the map, considering per-parameter resolution
    if (unifiedSchema.parameters) {
      unifiedSchema.parameters.forEach((param: Parameter) => {
        if (param.fullPath) {
          const conflict = detectedConflictingParameters.find(c => c.existingParam.fullPath === param.fullPath);
          const nonStandardField = detectedNonStandardFields.find(f => f.field === param.codeName && f.path === param.fullPath);

          let shouldKeepExisting = true;

          if (conflict) {
            const paramResolution = resolution?.parameterResolutions[param.fullPath];
            if (paramResolution === 'overwrite_new') {
              shouldKeepExisting = false;
            }
          }
          
          if (nonStandardField) {
            const nonStandardResolution = resolution?.nonStandardFieldResolutions[nonStandardField.field];
            if (nonStandardResolution === 'ignore') {
              shouldKeepExisting = false;
            }
          }

          if (shouldKeepExisting) {
            allUniqueParametersMap.set(param.fullPath, { ...param });
          }
        }
      });
    }

    // Add new parameters from uploaded files, applying resolution logic
    for (const { params: extractedParamsForFile } of newEventsWithParams) { // Iterate over extracted params for each new event
      for (const newParam of extractedParamsForFile) { // Iterate over params from this specific file
        if (newParam.fullPath) {
          const conflict = detectedConflictingParameters.find(c => c.newParam.fullPath === newParam.fullPath);
          const nonStandardField = detectedNonStandardFields.find(f => f.field === newParam.codeName && f.path === newParam.fullPath);

          let shouldAddNew = true;

          if (nonStandardField) {
            const nonStandardResolution = resolution?.nonStandardFieldResolutions[nonStandardField.field];
            if (nonStandardResolution === 'ignore') {
              shouldAddNew = false;
            }
          }

          if (conflict) {
            const paramResolution = resolution?.parameterResolutions[newParam.fullPath];
            if (paramResolution === 'keep_existing') {
              shouldAddNew = false;
            }
          }
          
          if (shouldAddNew) {
            allUniqueParametersMap.set(newParam.fullPath, { ...newParam, isPii: isPiiField(newParam.codeName) });
          }
        }
      }
    }

    // Reconstruct events with updated parameterFullPaths based on the final set of unique parameters
    const finalParameters = Array.from(allUniqueParametersMap.values());
    const updatedEvents: EventSchema[] = [];
    newEventsWithParams.forEach(({ event, params: originalExtractedParamsForEvent }) => {
      const updatedEventParams = originalExtractedParamsForEvent
        .filter(p => allUniqueParametersMap.has(p.fullPath)) // Only include parameters that made it into the final map
        .map(p => p.fullPath);
      updatedEvents.push({ ...event, parameterFullPaths: updatedEventParams });
    });


    // Determine scope for each unique parameter based on all events (existing + new)
    const allEventsForScopeDetermination = [...unifiedSchema.events, ...updatedEvents]; // Use updatedEvents here
    allUniqueParametersMap.forEach((param: Parameter) => {
      param.scope = determineParameterScope(param, allEventsForScopeDetermination);
    });

    const updatedUnifiedSchema: UnifiedSchema = {
      ...unifiedSchema,
      id: unifiedSchema.id as string, // Explicitly ensure id is string
      events: [...unifiedSchema.events, ...updatedEvents],
      parameters: finalParameters,
    };

    setUnifiedSchema(updatedUnifiedSchema);
    setHasUnsavedChanges(true);
    setParameterCount((count: number) => count + 1);

    if (updatedEvents.length > 0) { // Select the first newly added event
      setSelectedEvent(updatedEvents[0]);
    }
  }, [unifiedSchema, showNotification, setParameterCount, setConflictingParameters, setNonStandardFields, setPendingImportFiles, setShowImportConflictDialog]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    await processFiles(files);
  }, [processFiles]);

const handleImportResolution = useCallback(async (resolution: ImportResolution) => {
  setShowImportConflictDialog(false);
  if (pendingImportFiles.length > 0) {
    await processFiles(pendingImportFiles, resolution); // Re-process with resolution
    setPendingImportFiles([]); // Clear pending files
    setConflictingParameters([]); // Clear conflicts
    setNonStandardFields([]); // Clear non-standard fields
  }
}, [pendingImportFiles, processFiles, setShowImportConflictDialog, setPendingImportFiles, setConflictingParameters, setNonStandardFields]);

    const handleAddEmptyEvent = useCallback(async () => {
    if (!unifiedSchema) {
      console.error("Unified schema is not loaded. Cannot add event.");
      return;
    }

    const newEvent: EventSchema = {
      id: uuidv4(), // Generate UUID for new event
      name: '', // Empty name for new event
      codeName: '', // Re-added
      description: '', // Empty description for new event
      parameterFullPaths: [], // Initialize with empty array
      isNew: true, // Mark as new for potential auto-focus
    };
    
    const updatedEvents = [...unifiedSchema.events, newEvent];
    const updatedUnifiedSchema = { ...unifiedSchema, events: updatedEvents };
    
    setUnifiedSchema(updatedUnifiedSchema); // Update the unified schema state
    unifiedSchemaRef.current = updatedUnifiedSchema; // <--- IMMEDIATELY UPDATE THE REF
    
    setHasUnsavedChanges(true); // Mark as unsaved
    setSelectedEvent(newEvent); // Select the newly added event

    // Pass isNewSchemaCreation in state if the current unifiedSchema is new
    // No navigation needed when adding an event to an already loaded/new schema.
    // The URL should remain consistent with the UnifiedSchema ID.
  }, [unifiedSchema, isNewSchemaCreation, setUnifiedSchema, unifiedSchemaRef, setHasUnsavedChanges, setSelectedEvent]);

const handleAddParameter = useCallback((eventId: string, parentParam: Parameter | null, targetScope: 'global' | 'eventSpecific') => {
  setHasUnsavedChanges(true); // Mark as unsaved
  setUnifiedSchema((prevUnifiedSchema: UnifiedSchema | null) => {
    if (!prevUnifiedSchema) return null;

    // Create a new parameter with a unique ID and default values
    const newParam = createNewParameter(targetScope, parentParam);
    
    // Generate a default codeName if it's empty
    if (!newParam.codeName) {
      const defaultName = 'new_property';
      newParam.codeName = defaultName;
      
      // Update path and fullPath based on parent
      if (parentParam) {
        const parentPath = parentParam.path || '';
        newParam.path = `${parentPath}.${defaultName}`;
        newParam.fullPath = newParam.path;
      } else {
        newParam.path = defaultName;
        newParam.fullPath = defaultName;
      }
    }
    
    // Add the new parameter to the unifiedSchema.parameters
    const updatedParameters = [...prevUnifiedSchema.parameters, newParam]; // Changed from properties

    let updatedSelectedEvent: EventSchema | null = null; // Declare a variable to hold the updated selected event

    // Update the event to include the new parameter
    const updatedEvents = prevUnifiedSchema.events.map((event: EventSchema) => {
      if (event.id === eventId) { // Corrected: should compare event.id with eventId
        // Add the fullPath of the new parameter to the event's parameterFullPaths
        const updatedParameterFullPaths = [...event.parameterFullPaths, newParam.fullPath];
        
        // If adding a sub-parameter, ensure parent is expanded
        if (parentParam && !parentParam.isExpanded) {
          if (targetScope === 'global') {
            setGlobalTreeExpansion((prev: Record<string, boolean>) => ({ ...prev, [parentParam.path]: true }));
          } else {
            setEventSpecificTreeExpansion((prev: Record<string, boolean>) => ({ ...prev, [parentParam.path]: true }));
          }
        }

        // Set the ID of the new parameter to be edited
        setParamToEditId(newParam.id);

        const eventWithNewParam = { // Create the updated event object
          ...event,
          parameterFullPaths: updatedParameterFullPaths,
        };
        if (selectedEvent && selectedEvent.id === event.id) { // If this is the currently selected event
          updatedSelectedEvent = eventWithNewParam; // Store it
        }
        return eventWithNewParam;
      }
      return event;
    });

    const updatedUnifiedSchema = {
      ...prevUnifiedSchema,
      events: updatedEvents,
      parameters: updatedParameters // Changed from properties
    };

    // Update selectedEvent state immediately if it was the one modified
    if (updatedSelectedEvent) {
      setSelectedEvent(updatedSelectedEvent);
    }

    // Increment parameter count to force re-render of the tree
    setParameterCount((count: number) => count + 1);

    return updatedUnifiedSchema;
  });
}, [selectedEvent, setHasUnsavedChanges, setUnifiedSchema, setGlobalTreeExpansion, setEventSpecificTreeExpansion, setParamToEditId, setSelectedEvent, setParameterCount]);

  const handleDeleteParameter = useCallback((paramId: string, deleteFromAllEvents?: boolean) => {
    setHasUnsavedChanges(true); // Mark as unsaved
    setUnifiedSchema((prevUnifiedSchema: UnifiedSchema | null) => {
      if (!prevUnifiedSchema) return null;

      const paramToDelete = prevUnifiedSchema.parameters.find((p: Parameter) => p.id === paramId); // Changed from properties
      if (!paramToDelete || !paramToDelete.fullPath) return prevUnifiedSchema; // Ensure fullPath exists

      // Find all child parameters that need to be deleted as well
      const childParamsToDelete = prevUnifiedSchema.parameters.filter((p: Parameter) => // Changed from properties
        p.fullPath && p.fullPath.startsWith(`${paramToDelete.fullPath}.`)
      );
      
      // Collect all fullPaths that need to be removed (parent + children)
      const fullPathsToRemove = [
        paramToDelete.fullPath,
        ...childParamsToDelete.map((p: Parameter) => p.fullPath)
      ];

      let updatedEvents = [...prevUnifiedSchema.events];
      if (deleteFromAllEvents) {
        // Remove the fullPaths from all events
        updatedEvents = updatedEvents.map((event: EventSchema) => ({
          ...event,
          parameterFullPaths: event.parameterFullPaths.filter(
            (fullPath: string) => !fullPathsToRemove.includes(fullPath)
          ),
        }));
      } else if (selectedEvent) {
        // Remove the fullPaths only from the selected event
        updatedEvents = updatedEvents.map((event: EventSchema) => {
          if (event.id === selectedEvent.id) {
            return {
              ...event,
              parameterFullPaths: event.parameterFullPaths.filter(
                (fullPath: string) => !fullPathsToRemove.includes(fullPath)
              ),
            };
          }
          return event;
        });
      }

      // Check if the parameters are referenced by any other event after updates
      const referencedParams = new Set<string>();
      updatedEvents.forEach((event: EventSchema) => {
        event.parameterFullPaths.forEach((fullPath: string) => {
          referencedParams.add(fullPath);
        });
      });

      // Keep only parameters that are still referenced by at least one event
      // Remove the parameter and its children from the master list
      let updatedParameters = prevUnifiedSchema.parameters.filter((p: Parameter) => {
        return !(p.id === paramId || (paramToDelete.fullPath && p.fullPath && p.fullPath.startsWith(`${paramToDelete.fullPath}.`)));
      });

      const updatedUnifiedSchema = { ...prevUnifiedSchema, events: updatedEvents, parameters: updatedParameters }; // Changed from properties
      
      // Find and set the updated selected event from the new unified schema
      const newlySelectedEvent = updatedUnifiedSchema.events.find(e => e.id === selectedEvent?.id) || null;
      setSelectedEvent(newlySelectedEvent);

      // Increment parameter count to force re-render of the tree
      setParameterCount((count: number) => count + 1);
      
      return updatedUnifiedSchema;
    });
  }, [selectedEvent, setHasUnsavedChanges, setUnifiedSchema, setSelectedEvent, setParameterCount]);

  const handleParameterChange = useCallback((paramId: string, updates: Partial<Parameter>) => {
    setHasUnsavedChanges(true); // Mark as unsaved
    setUnifiedSchema((prevUnifiedSchema: UnifiedSchema | null) => {
      if (!prevUnifiedSchema) return null;

      // Find the parameter to update
      const paramToUpdate = prevUnifiedSchema.parameters.find((p: Parameter) => p.id === paramId); // Changed from properties
      if (!paramToUpdate) {
        return prevUnifiedSchema;
      }

      // Store old values for comparison (if needed for path updates)
      const oldFullPath = paramToUpdate.fullPath;
      
      // Apply updates to the parameter
      let updatedParam = { ...paramToUpdate, ...updates } as Parameter;

      // Determine if fullPath has changed
      const isFullPathChanged = updates.fullPath !== undefined && updates.fullPath !== oldFullPath;

      if (isFullPathChanged) {
        const newFullPath = updates.fullPath as string;
        const newFullPathSegments = newFullPath.split('.');
        const newCodeName = newFullPathSegments[newFullPathSegments.length - 1];
        const newParentPaths = newFullPathSegments.slice(0, -1);
        const newPath = newFullPath; // For root, path is fullPath; for children, path is fullPath

        updatedParam = {
          ...updatedParam,
          codeName: newCodeName,
          path: newPath,
          fullPath: newFullPath,
          parentPaths: newParentPaths,
          level: newParentPaths.length,
        };
      } else if (updates.codeName !== undefined && updates.codeName !== paramToUpdate.codeName) {
        // If only codeName changed (and fullPath was not directly updated)
        const newCodeName = updates.codeName as string;
        const parentPath = paramToUpdate.parentPaths && paramToUpdate.parentPaths.length > 0 
          ? paramToUpdate.parentPaths.join('.') 
          : '';
        const newFullPath = parentPath ? `${parentPath}.${newCodeName}` : newCodeName;
        
        updatedParam = {
          ...updatedParam,
          codeName: newCodeName,
          path: newFullPath, // path is now the same as fullPath
          fullPath: newFullPath,
        };
      }

      let updatedParameters = prevUnifiedSchema.parameters.map((p: Parameter) =>
        p.id === paramId ? updatedParam : p
      );

      // If fullPath or codeName changed, we need to update all child paths and event references
      if (isFullPathChanged || (updates.codeName !== undefined && updates.codeName !== paramToUpdate.codeName)) {
        const effectiveOldFullPath = oldFullPath; // Use the original fullPath for comparison
        const effectiveNewFullPath = updatedParam.fullPath; // Use the newly calculated fullPath

        updatedParameters = updatedParameters.map((p: Parameter) => {
          if (p.id === paramId) return p; // Already updated

          // Check if this is a child of the updated parameter
          if (p.fullPath && p.fullPath.startsWith(`${effectiveOldFullPath}.`)) {
            const relativePath = p.fullPath.substring(effectiveOldFullPath.length + 1);
            const newChildFullPath = `${effectiveNewFullPath}.${relativePath}`;
            const newChildFullPathSegments = newChildFullPath.split('.');
            
            return {
              ...p,
              path: newChildFullPath,
              fullPath: newChildFullPath,
              parentPaths: newChildFullPathSegments.slice(0, -1),
              level: newChildFullPathSegments.length - 1,
            };
          }
          return p;
        });

        // Update event references to the changed paths
        const updatedEvents = prevUnifiedSchema.events.map((event: EventSchema) => {
          const updatedParameterFullPaths = event.parameterFullPaths.map((fp: string) => {
            if (fp === effectiveOldFullPath) {
              return effectiveNewFullPath;
            } else if (fp.startsWith(`${effectiveOldFullPath}.`)) {
              const relativePath = fp.substring(effectiveOldFullPath.length + 1);
              const newFullPath = `${effectiveNewFullPath}.${relativePath}`;
              return newFullPath;
            }
            return fp;
          });
          
          return { 
            ...event, 
            parameterFullPaths: updatedParameterFullPaths 
          };
        });

        // Re-calculate scope for all parameters after path changes
        const allEventsForScopeDetermination = updatedEvents; // Use the updated events for scope calculation
        updatedParameters = updatedParameters.map(p => ({
          ...p,
          scope: determineParameterScope(p, allEventsForScopeDetermination)
        }));
        
        // Return the updated state with both parameters and events updated
        const finalUnifiedSchema = { 
          ...prevUnifiedSchema, 
          events: updatedEvents, 
          parameters: updatedParameters 
        };
        
        // Force re-render of the tree
        setParameterCount((count: number) => count + 1);
        
        return finalUnifiedSchema;
      }
      
      // If no path updates needed, just return with the updated parameters
      const finalUnifiedSchema = { 
        ...prevUnifiedSchema, 
        parameters: updatedParameters 
      };
      
      // Force re-render of the tree
      setParameterCount((count: number) => count + 1);
      
      return finalUnifiedSchema;
    });
  }, [setHasUnsavedChanges, setUnifiedSchema, setParameterCount]);

  // Debounced version for ParameterTreeNode to use with increased delay
  const debouncedHandleParameterChange = useMemo(
    () => debounce(handleParameterChange, 300),
    [handleParameterChange]
  );

  const handleParameterAssociationChange = useCallback((propertyFullPath: string, eventId: string, isAssociated: boolean) => {
    setHasUnsavedChanges(true);
    setUnifiedSchema((prevUnifiedSchema: UnifiedSchema | null) => {
      if (!prevUnifiedSchema) return null;

      const updatedEvents = prevUnifiedSchema.events.map((event: EventSchema) => {
        if (event.id === eventId) {
          const currentPaths = new Set(event.parameterFullPaths);
          if (isAssociated) {
            currentPaths.add(propertyFullPath);
          } else {
            currentPaths.delete(propertyFullPath);
          }
          return { ...event, parameterFullPaths: Array.from(currentPaths) };
        }
        return event;
      });

      // Check if the property is now orphaned (not associated with any event)
      const isReferencedByAnyEvent = updatedEvents.some((event: EventSchema) =>
        event.parameterFullPaths.includes(propertyFullPath)
      );

      let updatedParameters = [...prevUnifiedSchema.parameters]; // Changed from properties
      if (!isAssociated && !isReferencedByAnyEvent) {
        // If the property is being disassociated and is no longer referenced by any event,
        // prompt for deletion.
        setPropertyToDeleteFullPath(propertyFullPath);
        setShowDeletePropertyConfirmation(true);
        // Do NOT remove from updatedParameters here. It will be removed if confirmed.
      } else if (isAssociated && !prevUnifiedSchema.parameters.some((p: Parameter) => p.fullPath === propertyFullPath)) { // Changed from properties
        // If a property is being associated and it's not yet in the master list, add it.
        // This case should ideally be handled by handleAddParameter, but as a safeguard.
        const paramToAdd = prevUnifiedSchema.parameters.find((p: Parameter) => p.fullPath === propertyFullPath); // Changed from properties
        if (paramToAdd) {
          updatedParameters.push(paramToAdd);
        }
      }

      const updatedUnifiedSchema = { ...prevUnifiedSchema, events: updatedEvents, parameters: updatedParameters }; // Changed from properties
      setParameterCount((count: number) => count + 1); // Force re-render
      return updatedUnifiedSchema;
    });
  }, [setHasUnsavedChanges, setUnifiedSchema, setPropertyToDeleteFullPath, setShowDeletePropertyConfirmation, setParameterCount]);

  const confirmDeleteProperty = useCallback(() => {
    if (propertyToDeleteFullPath && unifiedSchema) {
      setUnifiedSchema((prevUnifiedSchema: UnifiedSchema | null) => {
        if (!prevUnifiedSchema) return null;

        // Find the property to delete
        const propertyToDelete = prevUnifiedSchema.parameters.find((p: Parameter) => // Changed from properties
          p.fullPath === propertyToDeleteFullPath
        );

        if (!propertyToDelete) return prevUnifiedSchema;

        // Find all child properties that need to be deleted as well
        const childPropertiesToDelete = prevUnifiedSchema.parameters.filter((p: Parameter) => // Changed from properties
          p.fullPath && p.fullPath.startsWith(`${propertyToDeleteFullPath}.`)
        );
        
        // Collect all fullPaths that need to be removed (parent + children)
        const fullPathsToRemove = [
          propertyToDeleteFullPath,
          ...childPropertiesToDelete.map((p: Parameter) => p.fullPath)
        ];

        // Remove the properties from the master list
        const updatedParameters = prevUnifiedSchema.parameters.filter((p: Parameter) => // Changed from properties
          !fullPathsToRemove.includes(p.fullPath)
        );

        // Remove the properties from all events' parameterFullPaths
        const updatedEvents = prevUnifiedSchema.events.map((event: EventSchema) => ({
          ...event,
          parameterFullPaths: event.parameterFullPaths.filter(
            (fp: string) => !fullPathsToRemove.includes(fp)
          ),
        }));

        return { ...prevUnifiedSchema, parameters: updatedParameters, events: updatedEvents }; // Changed from properties
      });
      setHasUnsavedChanges(true);
      setShowDeletePropertyConfirmation(false);
      setPropertyToDeleteFullPath(null);
      setParameterCount((count: number) => count + 1); // Force re-render
    }
  }, [propertyToDeleteFullPath, unifiedSchema, setHasUnsavedChanges, setShowDeletePropertyConfirmation, setPropertyToDeleteFullPath, setParameterCount]);

  const cancelDeleteProperty = useCallback(() => {
    setShowDeletePropertyConfirmation(false);
    
    // If the user cancels deletion, we need to re-associate the property with the event
    // that triggered the confirmation, to revert the change.
    if (propertyToDeleteFullPath && selectedEvent) {
      setUnifiedSchema((prevUnifiedSchema: UnifiedSchema | null) => {
        if (!prevUnifiedSchema) return prevUnifiedSchema;

        // Find the property that was going to be deleted
        const propertyToKeep = prevUnifiedSchema.parameters.find((p: Parameter) => // Changed from properties
          p.fullPath === propertyToDeleteFullPath
        );

        if (!propertyToKeep) return prevUnifiedSchema;

        // Find all child properties that would have been deleted as well
        const childPropertiesToKeep = prevUnifiedSchema.parameters.filter((p: Parameter) => // Changed from properties
          p.fullPath && p.fullPath.startsWith(`${propertyToDeleteFullPath}.`)
        );
        
        // Re-associate the property and its children with the selected event
        const updatedEvents = prevUnifiedSchema.events.map((event: EventSchema) => {
          if (event.id === selectedEvent.id) {
            // Get current paths
            const currentPaths = new Set(event.parameterFullPaths);
            
            // Add the parent property path
            currentPaths.add(propertyToDeleteFullPath);
            
            // Add all child property paths
            childPropertiesToKeep.forEach((child: Parameter) => {
              if (child.fullPath) {
                currentPaths.add(child.fullPath);
              }
            });
            
            return {
              ...event,
              parameterFullPaths: Array.from(currentPaths)
            };
          }
          return event;
        });

        return { ...prevUnifiedSchema, events: updatedEvents };
      });
      
      setHasUnsavedChanges(true); // Reverting is also a change
      setParameterCount((count: number) => count + 1); // Force re-render
    }
    
    setPropertyToDeleteFullPath(null);
  }, [propertyToDeleteFullPath, selectedEvent, setHasUnsavedChanges, setUnifiedSchema, setParameterCount, setPropertyToDeleteFullPath]);


  const { allAssociatedParameters } = useMemo(() => {
    const allAssociated: Parameter[] = [];
    const globalParams: Parameter[] = [];
    const eventSpecificParams: Parameter[] = [];

    if (unifiedSchema && selectedEvent) {
      const parametersToFilter = unifiedSchema.parameters ?? [];
      const explicitlyAssociatedPaths = new Set(selectedEvent.parameterFullPaths);
      const allAssociatedPaths = new Set<string>();

      // Add all explicitly associated paths and their ancestors
      explicitlyAssociatedPaths.forEach((fullPath: string) => {
        allAssociatedPaths.add(fullPath);
        let currentPathSegments = fullPath.split('.');
        while (currentPathSegments.length > 0) {
          currentPathSegments.pop(); // Remove last segment
          if (currentPathSegments.length > 0) {
            allAssociatedPaths.add(currentPathSegments.join('.'));
          }
        }
      });

      // Filter parameters from the master list based on the expanded set of associated paths
      const associatedParameters = parametersToFilter.filter((param: Parameter) => {
        const isAssociated = allAssociatedPaths.has(param.fullPath || '');
        return isAssociated;
      });

      associatedParameters.forEach((param: Parameter) => {
        allAssociated.push(param); // Add to combined list
        if (param.scope === 'global') {
          globalParams.push(param);
        } else {
          eventSpecificParams.push(param);
        }
      });
    }

    return { allAssociatedParameters: allAssociated, globalParameters: globalParams, eventSpecificParameters: eventSpecificParams };
  }, [unifiedSchema, selectedEvent, parameterCount]);

  // Build a single tree from all associated parameters
  const [fullTreeNodes, fullAllNodesMap] = useMemo(() => {
    // Combine global and event-specific expansion states for the full tree
    const combinedExpansionState = { ...globalTreeExpansion, ...eventSpecificTreeExpansion };
    return buildTreeStructure(allAssociatedParameters, combinedExpansionState);
  }, [allAssociatedParameters, globalTreeExpansion, eventSpecificTreeExpansion, buildTreeStructure]);

  // Filter the full tree nodes into global and event-specific for rendering
  const filteredGlobalTreeNodes = useMemo(() => {
    const filtered = filterParameters(fullTreeNodes, { ...filterOptions, showEventSpecific: false });
    // Ensure only actual global root nodes are shown
    return filtered.filter((node: Parameter) => node.scope === 'global');
  }, [fullTreeNodes, filterOptions]);

  const filteredEventSpecificTreeNodes = useMemo(() => {
    if (!selectedEvent) return [];
    const filtered = filterParameters(fullTreeNodes, { ...filterOptions, showGlobal: false });
    // Ensure only actual event-specific root nodes are shown
    return filtered.filter((node: Parameter) => node.scope === 'eventSpecific');
  }, [fullTreeNodes, filterOptions, selectedEvent]);

  // Pass the fullAllNodesMap to ParameterTreeNode for correct parent lookups
  const globalAllNodesMap = fullAllNodesMap;
  const eventSpecificAllNodesMap = fullAllNodesMap;

  const sourceName = nameFromState || schemaName || 'New Schema';
  const isEditable = unifiedSchema?.status === 'draft'; // Determine if the schema is editable

  const breadcrumbs = useMemo(() => {
    const typeIcon = getSchemaTypeIcon(schemaType || '');
    const typeDisplayName = getSchemaTypeDisplayName(schemaType || '');

    return [
      { name: typeDisplayName, iconName: typeIcon, path: `/labs/${(schemaType || '').replace('_schema', '-schemas')}` },
      { name: sourceName },
    ];
  }, [schemaType, sourceName]);

  const handleCancel = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowConfirmCancelDialog(true);
    } else {
      navigate(`/labs/${(schemaType || '').replace('_schema', '-schemas')}`);
    }
  }, [hasUnsavedChanges, navigate, setShowConfirmCancelDialog, schemaType]);

  const confirmCancel = useCallback(() => {
    clearStateSchemaEditor(); // Clear local storage changes
    navigate(`/labs/${(schemaType || '').replace('_schema', '-schemas')}`);
  }, [navigate, schemaType]);

  const handleSaveAsDraftSuccess = useCallback((newSchema: UnifiedSchema) => {
    setUnifiedSchema(newSchema);
    setHasUnsavedChanges(false);
    clearStateSchemaEditor(newSchema.id); // Clear local storage for this schema after successful save
    setShowSaveAsDraftDialog(false);
    showNotification('success', 'Schema saved as draft successfully'); // Add success notification
    // Navigate back to the corresponding list view
    navigate(`/labs/${(schemaType || '').replace('_schema', '-schemas')}`, { replace: true });
  }, [navigate, showNotification, setUnifiedSchema, setHasUnsavedChanges, setShowSaveAsDraftDialog, schemaType]);

  const handleSaveChanges = useCallback(async () => {
    if (!unifiedSchema) return;
    setIsSavingDraft(true); // Set saving state immediately
    await debouncedSave(unifiedSchema);
    // The debouncedSave already handles clearing local storage and notifications on success.

  }, [unifiedSchema, debouncedSave, setIsSavingDraft]);

  const handlePublishSchema = useCallback(async () => {
    if (!unifiedSchema) return;
    setIsPublishing(true);
    try {
      const updatedSchema = { ...unifiedSchema, status: 'published' as 'published' };
      const resultSchema = await updateUnifiedSchema(updatedSchema);
      if (resultSchema) {
        setUnifiedSchema(resultSchema);
        setHasUnsavedChanges(false);
        clearStateSchemaEditor(resultSchema.id); // Clear local storage for this schema after successful save
        showNotification('success', 'Schema published successfully'); // Add success notification
        navigate(`/labs/${(schemaType || '').replace('_schema', '-schemas')}`, { replace: true }); // Navigate back to definitions list
      } else {
        console.error('Failed to publish schema.');
        showNotification('error', 'Failed to publish schema.'); // Add error notification
      }
    } catch (error) {
      console.error('Error publishing schema:', error);
      showNotification('error', 'Error publishing schema.');
    } finally {
      setIsPublishing(false);
    }
  }, [unifiedSchema, showNotification, navigate, setUnifiedSchema, setHasUnsavedChanges, setIsPublishing]);

  const handleExport = useCallback((action: 'playbook-yaml' | 'connection-yaml' | 'combined-gz' | 'combined-yaml') => {
    if (!unifiedSchema) {
      showNotification('error', 'No schema to export.');
      return;
    }

    let content: string;
    let filename: string;
    const schemaNameForFile = unifiedSchema.name || 'schema';

    switch (action) {
      case 'playbook-yaml':
        // Placeholder: Export unifiedSchema as YAML for playbook-yaml
        content = yaml.dump(unifiedSchema, { indent: 2 });
        filename = `${schemaNameForFile}_playbook.yaml`;
        break;
      case 'connection-yaml':
        // Placeholder: Export unifiedSchema as YAML for connection-yaml
        content = yaml.dump(unifiedSchema, { indent: 2 });
        filename = `${schemaNameForFile}_connection.yaml`;
        break;
      case 'combined-gz':
        // Placeholder: Export unifiedSchema as JSON for combined-gz (will need actual GZ compression)
        content = JSON.stringify(unifiedSchema, null, 2);
        filename = `${schemaNameForFile}_combined.json`; // Should be .gz
        showNotification('warning', 'Combined GZ export is a placeholder and exports JSON.');
        break;
      case 'combined-yaml':
        // Placeholder: Export unifiedSchema as YAML for combined-yaml
        content = yaml.dump(unifiedSchema, { indent: 2 });
        filename = `${schemaNameForFile}_combined.yaml`;
        break;
      default:
        showNotification('error', `Unknown export action: ${action}`);
        return;
    }

    const blob = new Blob([content], { type: action.includes('json') ? 'application/json' : 'text/yaml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showNotification('success', `Schema exported as ${action}`);
  }, [unifiedSchema, showNotification]);

  const actions: ActionButton[] = useMemo(() => {
    const baseActions: ActionButton[] = [
      { label: 'Back', onClick: handleCancel, variant: 'text' },
    ];

    if (unifiedSchema?.status === 'draft') {
      baseActions.push(
        { 
          label: isSavingDraft
            ? 'Saving...'
            : (unifiedSchema.isNew ? 'Save as Draft' : 'Save Changes'), // Change label based on isNew
          onClick: unifiedSchema.isNew
            ? () => setShowSaveAsDraftDialog(true) // If new, still open dialog
            : handleSaveChanges, // If existing draft, save changes directly
          variant: 'secondary',
          disabled: isSavingDraft || !unifiedSchema || !hasUnsavedChanges,
        },
        { 
          label: isPublishing ? 'Publishing...' : 'Publish Schema',
          onClick: handlePublishSchema, // Call publish handler
          variant: 'primary',
          disabled: isPublishing || !unifiedSchema,
        }
      );
    } else if (unifiedSchema?.status === 'published') {
      baseActions.push(
        {
          label: 'Save as New Draft',
          onClick: () => setShowSaveAsDraftDialog(true), // Re-use save as draft dialog
          variant: 'secondary',
          disabled: isSavingDraft || !unifiedSchema || !hasUnsavedChanges,
        },
        {
          label: 'Save as New Published',
          onClick: () => setShowSaveAsPublishedDialog(true), // Open Save as Published dialog
          variant: 'primary',
          disabled: isPublishing || !unifiedSchema,
        }
      );
    }

    return baseActions;
  }, [unifiedSchema, isSavingDraft, isPublishing, handleCancel, handlePublishSchema, handleSaveChanges, setShowSaveAsDraftDialog, setShowSaveAsPublishedDialog, hasUnsavedChanges]);

  const handleSchemaRenameConfirm = useCallback(async (newName: string, newDescription: string) => {
      if (!unifiedSchema) return;

    setIsSavingDraft(true); // Use isSavingDraft for rename operation
    try {
      const updatedSchema: UnifiedSchema = {
        ...unifiedSchema,
        name: newName,
        codeName: normalizeCodeName(newName), // Update codeName when name changes
        description: newDescription,
      };
      let result: UnifiedSchema | null = null;
      if (updatedSchema.isNew) {
        result = await createUnifiedSchema(updatedSchema);
      } else {
        result = await updateUnifiedSchema(updatedSchema);
      }
      if (result) {
        setUnifiedSchema(result);
        setHasUnsavedChanges(true); // Mark as unsaved because the name/description changed
        showNotification('success', 'Schema renamed successfully');
      } else {
        showNotification('error', 'Failed to rename schema.');
      }
    } catch (error) {
      console.error('Error renaming schema:', error);
      showNotification('error', 'Error renaming schema.');
    } finally {
      setIsSavingDraft(false); // Reset isSavingDraft
    }
  }, [unifiedSchema, showNotification, setUnifiedSchema, setHasUnsavedChanges, setIsSavingDraft]);

  const handleSaveAsPublishedSuccess = useCallback((newSchema: UnifiedSchema) => {
    setUnifiedSchema(newSchema);
    setHasUnsavedChanges(false);
    clearStateSchemaEditor(newSchema.id); // Clear local storage for this schema after successful save
    setShowSaveAsPublishedDialog(false);
    showNotification('success', 'Schema saved as published successfully'); // Add success notification
    navigate(`/labs/${(schemaType || '').replace('_schema', '-schemas')}`, { replace: true }); // Navigate back to definitions list
  }, [navigate, showNotification, setUnifiedSchema, setHasUnsavedChanges, setShowSaveAsPublishedDialog, schemaType]);

  const handleIntegrationChange = useCallback((integrationId: string | null) => {
    setHasUnsavedChanges(true);
    setUnifiedSchema((prevUnifiedSchema) => {
      if (!prevUnifiedSchema) return null;
      return { ...prevUnifiedSchema, integrationId: integrationId || undefined }; // Ensure it's undefined if null
    });
    setSelectedIntegrationId(integrationId); // Update local state
  }, [setHasUnsavedChanges, setUnifiedSchema, setSelectedIntegrationId]);

  return (
    <SchemaEditorLayout
      breadcrumbs={breadcrumbs} // Keep for SchemaEditorLayout's internal use if any
      actions={actions} // Keep for SchemaEditorLayout's internal use if any
      currentSchema={unifiedSchema}
      onSchemaRenameConfirm={handleSchemaRenameConfirm}
      isLoading={isLoading}
      onExport={handleExport} // Pass handleExport to LabsAppBar
    >
      {/* Left Panel - Events List */}
      <EventListPanel
        unifiedSchema={unifiedSchema}
        selectedEvent={selectedEvent}
        setSelectedEvent={setSelectedEvent}
        isEditable={isEditable}
        handleDuplicateEvent={handleDuplicateEvent}
        handleDeleteEvent={handleDeleteEvent}
        handleAddEmptyEvent={handleAddEmptyEvent}
        processFiles={processFiles}
        isDragOver={isDragOver}
        handleDragOver={handleDragOver}
        handleDragLeave={handleDragLeave}
        handleDrop={handleDrop}
        isDesktopMedium={isDesktopMedium} // Pass the new prop
        isTablet={isTablet} // Pass the new prop
        isMobile={isMobile} // Pass the new prop
        integrations={integrations} // Pass integrations
        selectedIntegrationId={selectedIntegrationId} // Pass selectedIntegrationId
        onIntegrationChange={handleIntegrationChange} // Pass handler
      />

      {/* Right Panel - Schema Properties */}
      <div className="relative h-full w-full flex flex-col">
        <SchemaPropertiesPanel
          unifiedSchema={unifiedSchema}
          selectedEvent={selectedEvent}
          isEditable={isEditable}
          handleEventChange={handleEventChange}
          isEventNameEditing={isEventNameEditing}
          setIsEventNameEditing={setIsEventNameEditing}
          isEventCodeNameEditing={isEventCodeNameEditing}
          setIsEventCodeNameEditing={setIsEventCodeNameEditing}
          isEventDescriptionEditing={isEventDescriptionEditing}
          setIsEventDescriptionEditing={setIsEventDescriptionEditing}
          eventCodeNameError={eventCodeNameError}
          eventNameInputRef={eventNameInputRef}
          eventCodeNameInputRef={eventCodeNameInputRef}
          eventDescriptionInputRef={eventDescriptionInputRef}
          filteredGlobalTreeNodes={filteredGlobalTreeNodes}
          filteredEventSpecificTreeNodes={filteredEventSpecificTreeNodes}
          filterOptions={filterOptions}
          setFilterOptions={setFilterOptions}
          handleAddParameter={handleAddParameter}
          showFilterPanel={showFilterPanel}
          globalAllNodesMap={globalAllNodesMap}
          eventSpecificAllNodesMap={eventSpecificAllNodesMap}
          toggleTreeNode={toggleTreeNode}
          handleParameterChange={debouncedHandleParameterChange}
          handleDeleteParameter={handleDeleteParameter}
          paramToEditId={paramToEditId}
          clearParamToEditId={() => setParamToEditId(null)}
          activeEditingParamId={activeEditingParamId}
          setActiveEditingParamId={setActiveEditingParamId}
          allEvents={unifiedSchema?.events || []} // Pass all events
          handleParameterAssociationChange={handleParameterAssociationChange}
        />
      </div>
      <SchemaEditorDialogs
        showConfirmDialog={showConfirmDialog}
        eventToDelete={eventToDelete}
        cancelDeleteEvent={cancelDeleteEvent}
        confirmDeleteEvent={confirmDeleteEvent}
        showConfirmCancelDialog={showConfirmCancelDialog}
        cancelConfirmCancelDialog={() => setShowConfirmCancelDialog(false)}
        confirmCancel={confirmCancel}
        showSaveAsDraftDialog={showSaveAsDraftDialog}
        closeSaveAsDraftDialog={() => setShowSaveAsDraftDialog(false)}
        currentSchema={unifiedSchema}
        onSaveAsDraftSuccess={handleSaveAsDraftSuccess}
        allSchemaNames={allSchemaNames}
        showSaveAsPublishedDialog={showSaveAsPublishedDialog}
        closeSaveAsPublishedDialog={() => setShowSaveAsPublishedDialog(false)}
        onSaveAsPublishedSuccess={handleSaveAsPublishedSuccess}
        showDeletePropertyConfirmation={showDeletePropertyConfirmation}
        propertyToDeleteFullPath={propertyToDeleteFullPath}
        cancelDeleteProperty={cancelDeleteProperty}
        confirmDeleteProperty={confirmDeleteProperty}
        showImportConflictDialog={showImportConflictDialog}
        closeImportConflictDialog={() => setShowImportConflictDialog(false)}
        conflictingParameters={conflictingParameters}
        nonStandardFields={nonStandardFields}
        onImportResolution={handleImportResolution}
        showPersistenceConflictDialog={showPersistenceConflictDialog}
        handleConfirmLoadBackend={handleConfirmLoadBackend}
        handleConfirmLoadPersisted={handleConfirmLoadPersisted}
      />
    </SchemaEditorLayout>
  );
};

export default SchemaEditPage;
